import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import styled from "@emotion/styled";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, MenuItem } from "@mui/material";
import { useForm, FormProvider } from "react-hook-form";
import useCountry from "../../../../hooks/useCountry";
import useBranch from "../../../../hooks/useBranch";
import useStateMaster from "../../../../hooks/useStateMaster";
import SearchableAutoComplete from "../../../../components/SearchableAutoComplete";
import FilterReset from "../../../../components/FilterReset";
import { searchPayload, convertedValue } from "../../../../utils/common";
import { OperatorConstants } from "../../../../constants/operator.constants";
import ReactHookFormSelect from "../../../../components/ReactHookFormSelect";
import CustomTextField from "../../../../components/CustomTextField";
import { useDialog } from "../../../../contexts/DialogContext";

const currentPayload = {
  filters: [],
  pagination: {
    pageNumber: 1,
    pageSize: 100,
  },
  sortFields: [],
};

function AdvanceFilter(props) {
  const { unSetDialog } = useDialog();
  const initialValues = {
    countryNameOperator: "eq",
    stateNameOperator: "eq",
    countryName: "",
    stateName: "",
  };

  const { fields, payload, setPayload, setFilterResetFunc } = props;

  const { autoSearchCountry, countryAutoSearchResults, isLoadingAutoSearch } = useCountry();
  const { autoSearchState, stateAutoSearchResults } = useStateMaster();

  const [statePayload, setStatePayload] = useState(currentPayload);
  const [stateOptions, setStateOptions] = useState([]);
  const [stateMultiValue, setStateMultiValue] = useState(false);

  const [countryPayload, setCountryPayload] = useState(currentPayload);
  const [countryOptions, setCountryOptions] = useState([]);
  const [countryMultiValue, setCountryMultiValue] = useState(false);
  const [defaultCountry, setDefaultCountry] = useState([]);
  const [defaultState, setDefaultState] = useState([]);

  const { t } = useTranslation();
  const methods = useForm({
    defaultValues: initialValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { errors },
    control,
    watch,
    setValue,
  } = methods;

  useEffect(() => {
    setFilterResetFunc(() => reset);
  }, [reset]);

  const countryOpValue = watch("countryNameOperator");
  const stateOpValue = watch("stateNameOperator");

  const onSubmit = async (data) => {
    try {
      let filters = [];

      if (data.countryName != null && Object.keys(data.countryName).length != 0) {
        filters.push({
          fieldName: "countryId",
          fieldValue: convertedValue(data.countryName, "value").join(","),
          op: data.countryNameOperator,
          dataType: "int",
          condition: "and",
          isJoin: true,
        });
      }
      if (data.stateName != null && Object.keys(data.stateName).length != 0) {
        filters.push({
          fieldName: "stateId",
          fieldValue: convertedValue(data.stateName, "value").join(","),
          op: data.stateNameOperator,
          dataType: "int",
          condition: "and",
          isJoin: false,
        });
      }

      setPayload({
        ...payload,
        filters: filters,
      });
      unSetDialog("advance_filter");
    } catch (err) {
      console.log(err, "errerrerrerr");
    }
  };

  useEffect(() => {
    if (countryOpValue == "in" || countryOpValue == "nin") {
      setDefaultCountry([]);
      setValue("countryName", []);
      setCountryMultiValue(true);
    } else {
      setDefaultCountry({});
      setValue("countryName", {});
      setCountryMultiValue(false);
    }
  }, [countryOpValue]);
  useEffect(() => {
    if (stateOpValue == "in" || stateOpValue == "nin") {
      setDefaultState([]);
      setValue("stateName", []);
      setStateMultiValue(true);
    } else {
      setDefaultState({});
      setValue("stateName", {});
      setStateMultiValue(false);
    }
  }, [stateOpValue]);

  const fetchCountry = async () => {
    await autoSearchCountry(countryPayload);
  };
  useEffect(() => {
    fetchCountry();
  }, [countryPayload]);

  const fetchState = async () => {
    await autoSearchState(statePayload);
  };
  useEffect(() => {
    fetchState();
  }, [statePayload]);

  useEffect(() => {
    if (countryAutoSearchResults.data.length !== 0) {
      setCountryOptions([...countryOptions, ...countryAutoSearchResults.data]);
    }
  }, [countryAutoSearchResults]);

  useEffect(() => {
    if (stateAutoSearchResults.data.length !== 0) {
      setStateOptions([...stateOptions, ...stateAutoSearchResults.data]);
    }
  }, [stateAutoSearchResults]);

  return (
    <React.Fragment>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={12} pt={8}>
            <Grid item xs={9.5} sm={12} md={6} lg={6} xl={4}>
              <ReactHookFormSelect
                name="countryNameOperator"
                label={t("Operator")}
                control={control}
                fullWidth
                variant="outlined"
                fields={fields}
                errors={errors}
              >
                {Object.entries(OperatorConstants.dropdown).map((value, k) => (
                  <MenuItem key={k} value={value[0]}>
                    {value[1]}
                  </MenuItem>
                ))}
              </ReactHookFormSelect>
            </Grid>
            <Grid item xs={9.5} sm={12} md={6} lg={6} xl={4}>
              <SearchableAutoComplete
                control={control}
                data={countryOptions}
                label={t("country_name")}
                payload={countryPayload}
                setPayload={setCountryPayload}
                loading={isLoadingAutoSearch}
                name="countryName"
                multiple={countryMultiValue}
                defaultValue={defaultCountry}
              />
            </Grid>

            <Grid item xs={9.5} sm={12} md={6} lg={6} xl={4}>
              <ReactHookFormSelect
                name="stateNameOperator"
                label={t("Operator")}
                control={control}
                fullWidth
                variant="outlined"
                fields={fields}
                errors={errors}
              >
                {Object.entries(OperatorConstants.dropdown).map((value, k) => (
                  <MenuItem key={k} value={value[0]}>
                    {value[1]}
                  </MenuItem>
                ))}
              </ReactHookFormSelect>
            </Grid>
            <Grid item xs={9.5} sm={12} md={6} lg={6} xl={4}>
              <SearchableAutoComplete
                control={control}
                data={stateOptions}
                label={t("state_name")}
                payload={statePayload}
                setPayload={setStatePayload}
                loading={isLoadingAutoSearch}
                name="stateName"
                multiple={stateMultiValue}
                defaultValue={defaultState}
              />
            </Grid>
          </Grid>
          <FilterReset resetForm={reset} />
        </form>
      </FormProvider>
    </React.Fragment>
  );
}

export default AdvanceFilter;
